import {createSelector} from 'reselect';
import lodashGet from 'lodash/get';
import {sortAlphabeticallyByPath} from '../../utils/sortingHelpers';
import {
  buildProjectUsersArray,
  getEntityNameByIdHelper,
  getUserNameWithCompanyHelper,
  isUserActive,
  parseUserObjectForCommentComponent
} from '../../utils/userHelpers';
import {ROLE_STATUS} from '../../constants/roles-types';
import {getSimpleGroupedEntities} from '../../utils/assigned-to-helpers';
import {stringifyQuery} from '../../utils/query-helpers';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {preloadedFilterQueryParamsState} from '../../constants/preloaded-states';
import lodashIsEqual from 'lodash/isEqual';
import {ISSUE_FILTER_PROPERTIES} from '../../constants/issue-query-params';
import _ from 'lodash';

export const getConfiguration = state => state.configuration;
export const getTargetUrn = state => state.targetUrn;
export const getHiddenQueryParams = state => state.hiddenQueryParams;
export const getCurrentViewType = state => state.currentView;
export const getDeletedUsers = state => state.deletedUsers.data;
export const getDeletedUsersFetched = state => state.deletedUsers.fetched;

const getLimit = () => 50;

export const getQueryParams = (state, issueType = TYPE_QUALITY_ISSUES) => state.queryParams[issueType];

export const isFiltered = createSelector(
  getQueryParams,
  getCurrentViewType,
  ({sort, search, offset, ...queryParams}, viewType) => {
    const {
      // eslint-disable-next-line no-unused-vars
      offset: preloadedOffset,
      ...preloadedFilterStateWithoutOffset
    } = preloadedFilterQueryParamsState[viewType][TYPE_QUALITY_ISSUES];
    return !lodashIsEqual(preloadedFilterStateWithoutOffset, queryParams);
  }
);

export const isFilteredByCustomAttributes = createSelector(
  getQueryParams,
  queryParams =>
    !!(queryParams[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]
      ? JSON.parse(queryParams[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES])
      : []
    ).length
);

export const isSearchOn = createSelector(
  getQueryParams,
  queryParams => !!queryParams.search
);

export const getDocumentsFetchingVersions = createSelector(
  getConfiguration,
  configuration => configuration.documentVersions.fetching
);

export const getCurrentSheets = createSelector(
  getConfiguration,
  ({sheetsConfig}) => sheetsConfig.currentSheets
);

export const getOxygenMappings = createSelector(
  getConfiguration,
  ({oxygen}) => {
    if (!oxygen) {
      return null;
    }

    const rolesInProject = {};
    Object.keys(oxygen.roles).forEach(roleId => {
      if (oxygen.roles[roleId].isInProject) {
        rolesInProject[roleId] = oxygen.roles[roleId];
      }
    });

    return {
      ...oxygen,
      roles: rolesInProject
    };
  }
);

export const getAllOxygenMappings = createSelector(
  getConfiguration,
  ({oxygen}) => oxygen
);

export const getProjectId = createSelector(
  getConfiguration,
  ({jsonapi}) => jsonapi.projectId
);

export const getAnalyticsId = createSelector(
  getConfiguration,
  ({analyticsConfig}) => analyticsConfig.analyticsId
);

export const getAnalyticsHostingAppName = createSelector(
  getConfiguration,
  ({analyticsConfig}) => analyticsConfig.hostingAppName
);

export const getEnv = createSelector(
  getConfiguration,
  ({jsonapi}) => jsonapi.env
);

export const getRegion = createSelector(
  getConfiguration,
  ({jsonapi}) => jsonapi.region
);

export const getFetchingIssuesUrns = createSelector(
  getConfiguration,
  ({fetchingIssuesUrns}) => fetchingIssuesUrns
);

export const getFetchingChecklistInstances = createSelector(
  getConfiguration,
  ({fetchingChecklistInstances}) => fetchingChecklistInstances
);

// "current" document should be same as one for given container-id/project-id.
// TODO: Refactor so current-document is retrieved solely from document-collection in Redux state:
export const getCurrentDocument = createSelector(
  getConfiguration,
  ({document}) => document
);

export const getDocumentCollection = createSelector(
  getConfiguration,
  getTargetUrn,
  getCurrentDocument,
  ({documentCollection}, targetUrn, currentDocument) => {
    if (targetUrn && !documentCollection[targetUrn]) {
      // Add current document in case it isn't there already. This can happen when current document doesn't have any issues yet.
      const fixedDocumentCollection = Object.assign({}, documentCollection);
      fixedDocumentCollection[targetUrn] = currentDocument;
      return fixedDocumentCollection;
    }
    return documentCollection;
  }
);

export const getCurrentDocumentVersion = createSelector(
  getCurrentDocument,
  document => (document ? document.version : null)
);

export const getDocumentsVersions = createSelector(
  getConfiguration,
  config => config.documentVersions.versions
);

export const getDocumentVersions = createSelector(
  getDocumentsVersions,
  (state, urn) => urn,
  (versions, urn) => versions[urn]
);

// getFetchParams passes state into primitive selectors:
export const getFetchParams = createSelector(
  getLimit,
  getTargetUrn,
  getQueryParams,
  getHiddenQueryParams,
  getCurrentDocumentVersion,
  (limit, targetUrn, queryParams, hiddenQueryParams, documentVersion) => {
    const {offset} = queryParams;
    return {offset, limit, targetUrn, queryParams, hiddenQueryParams, documentVersion};
  }
);

export const getDocumentRevisionByVersion = createSelector(
  getDocumentsVersions,
  (state, props) => props,
  (documentsVersions, {targetUrn, version}) => lodashGet(documentsVersions, [targetUrn, version, 'revisionNumber'])
);

export const getDocumentRevision = createSelector(
  getDocumentCollection,
  getDocumentsVersions,
  (state, targetUrn) => targetUrn,
  (documentCollection, documentsVersions, targetUrn) => {
    const version = lodashGet(documentCollection, [targetUrn, 'version']);
    return lodashGet(documentsVersions, [targetUrn, version, 'revisionNumber']);
  }
);

export const getAllUsers = createSelector(
  getOxygenMappings,
  oxygen => sortAlphabeticallyByPath(buildProjectUsersArray(oxygen.users), 'name')
);

export const getActiveUsers = createSelector(
  getAllUsers,
  users => users.filter(user => isUserActive(user))
);

export const getActiveAdminUsersSortedByName = createSelector(
  getActiveUsers,
  users => sortAlphabeticallyByPath(users.filter(user => user.isProjectAdmin), 'name')
);

export const getProjectRoles = createSelector(
  getOxygenMappings,
  oxygen => sortAlphabeticallyByPath(Object.values(oxygen.roles), 'name')
);

export const getAllRoles = createSelector(
  getAllOxygenMappings,
  oxygen => sortAlphabeticallyByPath(Object.values(oxygen.roles), 'name')
);

export const getActiveRoles = createSelector(
  getAllRoles,
  roles => roles.filter(role => role.status === ROLE_STATUS.ACTIVE)
);

export const getProjectCompanies = createSelector(
  getOxygenMappings,
  oxygen => sortAlphabeticallyByPath(Object.values(oxygen.companies), 'name')
);

const getUsersGroupByCompany = createSelector(
  getActiveUsers,
  users =>
    users.reduce((result, user) => {
      const company = user.company;
      if (company?.id) {
        if (result[company.id]) {
          result[company.id].push(user.id);
        } else {
          result[company.id] = [user.id];
        }
      }
      return result;
    }, {})
);

const getCurrentAssingees = createSelector(
  getFetchParams,
  query => query.queryParams[ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER] ?? []
);

// To get all assignees, companies and all their users
export const getAllAssignees = createSelector(
  getUsersGroupByCompany,
  getCurrentAssingees,
  (userIdsGroupByCompany, currentAssignees) =>
    currentAssignees.reduce((result, id) => {
      userIdsGroupByCompany[id] && result.push(...userIdsGroupByCompany[id]);
      return result;
    }, [...currentAssignees])
);
export const getGroupedProjectAllEntities = createSelector(
  getAllUsers,
  getProjectCompanies,
  getProjectRoles,
  (users, companies, roles) => getSimpleGroupedEntities({users, companies, roles})
);

export const getGroupedProjectActiveEntities = createSelector(
  getActiveUsers,
  getProjectCompanies,
  getProjectRoles,
  (users, companies, roles) => getSimpleGroupedEntities({users, companies, roles})
);

export const getAllProjectEntities = createSelector(
  getAllOxygenMappings,
  oxygen =>
    Object.values(oxygen.users)
      .concat(Object.values(oxygen.roles))
      .concat(Object.values(oxygen.companies))
);

export const getGroupedProjectActiveEntitiesForMentionComponent = createSelector(
  getGroupedProjectActiveEntities,
  projectEntities => {
    if (!projectEntities.length) {
      return projectEntities;
    }

    // getGroupedProjectActiveEntities builds a 3 cells array - [users, companies, roles].
    // Each array cell is an array which has 2 cells [title, objectsArray].
    // We only want to manipulate the users array and keep companies and roles as is.
    const users = projectEntities[0].slice();
    const usersForMentionComponent = users[1].map(user => parseUserObjectForCommentComponent(user));
    users[1] = usersForMentionComponent;

    return [users].concat([projectEntities[1]]).concat([projectEntities[2]]);
  }
);

export const getQueryKey = createSelector(
  getFetchParams,
  params => {
    // params is a nested object and stringifyQuery does not support nested objects (which is a good behavior).
    // Therefore, we're "flattening" the object by extracting queryParams, stringifying both objects and concating them.
    const filterParams = Object.assign({}, params.queryParams);
    const otherParams = Object.assign({}, params);
    delete otherParams['queryParams'];
    return `${stringifyQuery(filterParams)}&${stringifyQuery(otherParams)}`;
  }
);

export const getQueryKeyIsCurrent = createSelector(
  getQueryKey,
  (state, type, queryKey) => queryKey, // pass-through 3rd argument from higher-order/composite selector
  (currentQueryKey, queryKey) => currentQueryKey === queryKey
);

// Returns a function with 2 arguments already set, to get display name
export const getEntityUserNameGetter = createSelector(
  getOxygenMappings,
  getDeletedUsers,
  (oxygen, deletedUsers) => _.partial(getEntityNameByIdHelper, oxygen, deletedUsers, _, _, _)
);
export const getUserNameWithCompanyGetter = createSelector(
  getOxygenMappings,
  getDeletedUsers,
  (oxygen, deletedUsers) => _.partial(getUserNameWithCompanyHelper, oxygen, deletedUsers, _, _, _)
);
